import React, { useState, useEffect } from 'react';
import { clientApi } from 'api';
import { IPost } from 'client/models/IPost';
import './PostSelector.scss';

interface PostSelectorProps {
  value?: string; // ID du Post sélectionné
  onChange: (postId: string | null, post: IPost | null) => void;
  label?: string;
  placeholder?: string;
  videoType?: 'shortVideo' | 'mainVideo' | 'both';
}

const PostSelector: React.FC<PostSelectorProps> = ({
  value,
  onChange,
  label = 'Sélectionner un Post',
  placeholder = 'Rechercher un post par nom...',
  videoType = 'both',
}) => {
  const [posts, setPosts] = useState<IPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<IPost[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<IPost | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Charger les posts
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const allPosts: IPost[] = [];
        let page = 1;
        let hasNextPage = true;
        let totalPages = Number.POSITIVE_INFINITY;
        const MAX_PAGES = 200;

        while (hasNextPage && page <= totalPages && page <= MAX_PAGES) {
          const response = await clientApi.get('/posts/get-videos-list', {
            params: { page },
          });

          const payload = response.data;
          const items: IPost[] = Array.isArray(payload)
            ? payload
            : (payload?.items as IPost[]) || (payload?.data as IPost[]) || [];

          allPosts.push(...items);

          const nextHasNextPage = payload?.hasNextPage;
          const nextTotalPages = payload?.totalPages;
          const nextPage = payload?.page;

          hasNextPage = typeof nextHasNextPage === 'boolean' ? nextHasNextPage : items.length > 0;
          totalPages = typeof nextTotalPages === 'number' ? nextTotalPages : totalPages;
          page = typeof nextPage === 'number' ? nextPage + 1 : page + 1;

          if (!Array.isArray(payload) && items.length === 0) break;
        }

        // Filtrer selon le type de vidéo requis
        if (videoType === 'shortVideo') {
          const filtered = allPosts.filter((post: IPost) => post.shortVideo);
          setPosts(filtered);
          setFilteredPosts(filtered);
        } else if (videoType === 'mainVideo') {
          const filtered = allPosts.filter((post: IPost) => post.mainVideo);
          setPosts(filtered);
          setFilteredPosts(filtered);
        } else {
          setPosts(allPosts);
          setFilteredPosts(allPosts);
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [videoType]);

  // Charger le post sélectionné si value fourni
  useEffect(() => {
    if (value && posts.length > 0) {
      const post = posts.find((p) => p._id === value);
      if (post) {
        setSelectedPost(post);
      }
    }
  }, [value, posts]);

  // Filtrer les posts selon la recherche
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter((post) =>
        post.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPosts(filtered);
    }
  }, [searchTerm, posts]);

  const handleSelect = (post: IPost) => {
    setSelectedPost(post);
    onChange(post._id, post);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSelectedPost(null);
    onChange(null, null);
  };

  return (
    <div className="post-selector">
      <label className="form-label">{label}</label>

      {selectedPost ? (
        <div className="selected-post">
          <div className="post-card">
            <div className="post-thumbnail">
              {selectedPost.mainVideo?.thumbnailUrl ? (
                <img src={selectedPost.mainVideo.thumbnailUrl} alt={selectedPost.name} />
              ) : (
                <div className="no-thumbnail">
                  <i className="fas fa-video"></i>
                </div>
              )}
            </div>
            <div className="post-info">
              <h6>{selectedPost.name}</h6>
              <div className="post-meta">
                {selectedPost.shortVideo && (
                  <span className="badge bg-info me-1">
                    <i className="fas fa-film me-1"></i>
                    Short
                  </span>
                )}
                {selectedPost.mainVideo && (
                  <span className="badge bg-primary">
                    <i className="fas fa-video me-1"></i>
                    Main
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleClear}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      ) : (
        <div className="post-search">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setIsOpen(!isOpen)}
            >
              <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
            </button>
          </div>

          {isOpen && (
            <div className="post-dropdown">
              {loading ? (
                <div className="text-center p-3">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              ) : filteredPosts.length > 0 ? (
                <div className="post-list">
                  {filteredPosts.map((post) => (
                    <div
                      key={post._id}
                      className="post-item"
                      onClick={() => handleSelect(post)}
                    >
                      <div className="post-thumbnail-small">
                        {post.mainVideo?.thumbnailUrl ? (
                          <img src={post.mainVideo.thumbnailUrl} alt={post.name} />
                        ) : (
                          <div className="no-thumbnail">
                            <i className="fas fa-video"></i>
                          </div>
                        )}
                      </div>
                      <div className="post-info-small">
                        <span className="post-name">{post.name}</span>
                        <div className="post-badges">
                          {post.shortVideo && (
                            <span className="badge-mini bg-info">Short</span>
                          )}
                          {post.mainVideo && (
                            <span className="badge-mini bg-primary">Main</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-3 text-muted">
                  <i className="fas fa-search mb-2"></i>
                  <p className="mb-0">Aucun post trouvé</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostSelector;
